import { registerLocaleData } from '@angular/common';
import localeDE from '@angular/common/locales/de';
import localeCH from '@angular/common/locales/de-CH';
import localeFR from '@angular/common/locales/fr-CH';
import localeIT from '@angular/common/locales/it-CH';
import { Pipe, PipeTransform } from '@angular/core';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';

registerLocaleData(localeCH);
registerLocaleData(localeDE);
registerLocaleData(localeFR);
registerLocaleData(localeIT);
const debug = Debug('aumico:aumico-lib-core-fe:DateLocalePipe');

@Pipe({
    name: 'dateLocale',
})
export class DateLocalePipe implements PipeTransform {
    constructor(private i18NextService: I18NextService) {}

    /**
     * Transform a date to a localized string
     * @param value - Date string or Date object
     * @param incrementBy - Number of days to add/subtract
     * @param language - Optional language override
     * @param includeTime - Whether to include time in the output
     * @param useLocalTimezone - If true, uses local timezone; if false (default), uses UTC to prevent shifting calendar dates
     * @param roundToNearestDay - If true, snaps the instant to the nearest UTC midnight before formatting. This recovers
     *   the intended calendar day from legacy values that were stored at local midnight (e.g. 2024-12-30T23:00:00Z = 31.12
     *   in CET) without consulting any timezone; clean midnight values are unaffected. Ignored when includeTime is set.
     */
    transform(
        value: string | Date, 
        incrementBy?: number, 
        language?: string, 
        includeTime?: boolean,
        useLocalTimezone?: boolean,
        roundToNearestDay?: boolean
    ) {
        try {
            const dateOptions: Intl.DateTimeFormatOptions = { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit',
                ...(!useLocalTimezone && { timeZone: 'UTC' }), // Use UTC by default to prevent timezone shifting for calendar dates
                ...(includeTime && { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    hour12: false 
                })
            };
            let date = new Date(value);
            if (incrementBy != null) {
                if (useLocalTimezone) {
                    date.setDate(date.getDate() + incrementBy);
                } else {
                    date.setUTCDate(date.getUTCDate() + incrementBy);
                }
            }

            if (roundToNearestDay && !includeTime && !isNaN(date.getTime())) {
                const MS_PER_DAY = 24 * 60 * 60 * 1000;
                date = new Date(Math.round(date.getTime() / MS_PER_DAY) * MS_PER_DAY);
            }

            const formatLanguage = language == null ? this.i18NextService.language : language;

            const dateTimeFormat = new Intl.DateTimeFormat(formatLanguage, dateOptions);
            const dateLocale = dateTimeFormat.format(date);
            return dateLocale;
        } catch (error) {
            debug('try to convert a value, which is no date...', value);
            return value;
        }
    }
}
